import { AsyncPipe, CommonModule, formatNumber, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbInputModule,
  NbOptionModule,
  NbPopoverDirective,
  NbPopoverModule,
  NbSelectModule,
  NbSpinnerModule,
  NbTabsetModule,
  NbTooltipModule,
} from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, finalize, first, map, switchMap, tap } from 'rxjs/operators';
import { APIService, KPI } from '../../../../@core/api';
import { GROUP_PERMISSION_ACCESS_AUDIT, GROUP_PERMISSION_EDIT_QUOTE_PART, LoadingState, QuotePartItem } from '../../../../@core/api/models';
import { IntegerOnlyDirective } from '../../../../@core/directives';
import {
  AssetsService,
  ChartsService,
  ConfigService,
  DocumentsService,
  InitiativesService,
  InvestmentCategoriesService,
  InvestmentReasonsService,
  InvestmentsChangesService,
  InvestmentsService,
  InvestmentStatusesService,
  PerimetersService,
  QuotePartService,
  RoadmapService,
  SaveToastService,
  ScopeService,
  StateLevelsService,
  UsersService,
} from '../../../../@core/utils';
import { InvestmentWizardService } from '../../../../@core/utils/investment-wizard-service';
import { idMapToArray, toLoadingState } from '../../../../@core/utils/scope.service';
import { EntityDocumentsTabComponent } from '../../entity-documents-tab/entity-documents-tab.component';
import { BaseFormComponent } from '../base-form/base-form.component';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';
import { DialogWithCheckboxOptionComponent } from '../../dialog-with-checkbox-option/dialog-with-checkbox-option.component';
import { SheetDialogComponent } from '../../sheet-dialog/sheet-dialog.component';
import { BudgetAllocationComponent } from '../../wizards/investment-wizard/steps/budget-allocation.component';
import { CategorySelectorComponent } from '../../wizards/investment-wizard/steps/category-selector.component';
import { GainEnergyCarbonComponent } from '../../wizards/investment-wizard/steps/gain-energy-carbon.component';
import { MonoPerimeterSelectionComponent } from '../../wizards/investment-wizard/steps/mono-perimeter-selector.component';
import { TaxonomySelectorComponent } from '../../wizards/investment-wizard/steps/taxonomy-selector.component';
import { InvestmentWizardRootComponent } from '../../wizards/investment-wizard/wizard-index.component';
import { InvestmentNotationStepState, NomenclatureTechniqueStepState } from '../../wizards/investment-wizard/wizard-interface';
import { LinkableInvestmentsListComponent } from '../linkable-investments-list/linkable-investments-list.component';
import { SideFormDialogComponent } from '../side-form-dialog/side-form-dialog.component';
import { I18nService } from './../../../../@core/utils/i18n.service';
import { RightPanelDialogService } from './../../../../@core/utils/right-panel-dialog.service';
import { LinkedEquipmentDialogComponent } from '../../linked-equipment-dialog/linked-equipment-dialog.component';
import { InvestmentWizardQuickEditComponent } from '../../wizards/investment-wizard/wizard-quick-edit.component';

@Component({
  selector: 'app-investment-form-wrapper',
  template: `
      <nb-card style="width: 45vw">
        @if (type === 'BUDGET_ALLOCATION') {
          <nb-card-header>
            <div class="title">{{ 'Budget' | translate }}</div>
            <div class="cancel-button" (click)="close()">
              <i class="fal fa-x"></i>
            </div>
          </nb-card-header>
        }
        @if (type === 'BUDGET_ALLOCATION') {
          <nb-card-body>
            <app-wizard-budget-allocation #budgetAllocation
                                          [showStatusFormControl]="true"
                                          [allowedStatuses]="allowedStatuses"
                                          [readOnly]="readOnly"
                                          (formChanged)="onFormChanged()"></app-wizard-budget-allocation>
          </nb-card-body>
        } @else if (type === 'GAIN_ENERGY_CARBON') {
          <div class="scrolling-wrapper">
            <app-gain-energy-carbon #gainEnergyCarbon (formChanged)="onFormChanged()" (closed)="confirmClose()" />
          </div>
        } @else if (type === 'MONO_PERIMETER_SELECTION') {
          <app-wizard-mono-perimeter-selection />
        } @else if (type === 'TAXONOMY_SELECTION') {
          <app-taxonomy-selector
            [investment]="investment"
            [readOnly]="readOnly"
            (save)="onTaxonomySave($event)" />
        } @else if (type === 'CATEGORY_SELECTION') {
          <app-category-selector
            [investment]="investment"
            [readOnly]="readOnly"
            (save)="onCategorySave($event)" />
        }
        @if (shouldShowSaveButton()) {
          <nb-card-footer class="investment wizard">
            <button nbButton class="wizard-next" (click)="submit()" [disabled]="!isFormValid()">{{ 'Save' | translate }}
            </button>
          </nb-card-footer>
        }
      </nb-card>
  `,
  styleUrls: ['../base-form/base-form.component.scss', './investment-form-wrapper.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    TranslateModule,
    BudgetAllocationComponent,
    GainEnergyCarbonComponent,
    MonoPerimeterSelectionComponent,
    TaxonomySelectorComponent,
    CategorySelectorComponent,
  ],
})
export class InvestmentFormWrapperComponent implements OnInit, OnDestroy {

  @Input({ required: true }) investment;
  @Input({ required: true }) type!: 'BUDGET_ALLOCATION' | 'GAIN_ENERGY_CARBON' | 'MONO_PERIMETER_SELECTION' | 'TAXONOMY_SELECTION' | 'CATEGORY_SELECTION';
  @Input() readOnly: boolean = false;
  @Input() forceReadOnly: boolean = false;
  @Input() allowedStatuses: KPI.InvestmentStatus[] = [];

  @ViewChild('budgetAllocation') budgetAllocationComponent?: BudgetAllocationComponent;
  @ViewChild('gainEnergyCarbon') gainEnergyCarbonComponent?: GainEnergyCarbonComponent;

  private iVWizardService = inject(InvestmentWizardService);
  private invService = inject(InvestmentsService);
  private iniService = inject(InitiativesService);
  private ref = inject(NbDialogRef<InvestmentFormWrapperComponent>);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  private dialogService = inject(NbDialogService);
  private chartsService = inject(ChartsService);
  private saveToast = inject(SaveToastService);
  private documentsService = inject(DocumentsService);
  private subscriptions: Subscription[] = [];
  private lastSavedInvestment: any = null;
  private initialReadOnly: boolean = false;

  constructor() {
    effect(() => {
      const currentStep = this.iVWizardService.currentStep();
      if (this.type === 'MONO_PERIMETER_SELECTION' && currentStep.key !== 'MONO_PERIMETER_SELECTION') {
        untracked(() => this.ref.close());
      }
    });
  }

  onFormChanged(): void {
    this.cdr.detectChanges();
  }

  close(): void {
    this.ref.close(this.lastSavedInvestment ? { investment: this.lastSavedInvestment } : undefined);
  }

  ngOnInit() {
    this.initializeBackdropClick();
    this.iVWizardService.investmentId = this.investment.id;
    this.initialReadOnly = this.forceReadOnly;
  }

  shouldShowSaveButton(): boolean {
    if (this.type === 'BUDGET_ALLOCATION' && this.budgetAllocationComponent) {
      return this.budgetAllocationComponent.anyFieldValueChanged();
    }
    if (this.readOnly) {
      return false;
    }
    if (this.type === 'MONO_PERIMETER_SELECTION' || this.type === 'TAXONOMY_SELECTION') {
      return false;
    }
    if (this.type === 'GAIN_ENERGY_CARBON' && this.gainEnergyCarbonComponent) {
      return this.gainEnergyCarbonComponent.anyFieldValueChanged();
    }
    return false;
  }

  onTaxonomySave(result: {
    taxonomy_objective?: any;
    taxonomy_category?: any;
    taxonomy_comment?: string;
  }) {
    this.ref.close(result);
  }

  onCategorySave(result: {
    super_category?: any;
    category?: any;
    sub_category?: any;
  }) {
    this.ref.close(result);
  }

  isFormValid(): boolean {
    if (this.type === 'BUDGET_ALLOCATION' && this.budgetAllocationComponent) {
      return this.budgetAllocationComponent.isFormValid();
    }
    if (this.type === 'GAIN_ENERGY_CARBON' && this.gainEnergyCarbonComponent) {
      return this.gainEnergyCarbonComponent.isFormValid();
    }
    return true;
  }

  submit() {
    if (this.type === 'BUDGET_ALLOCATION') {
      const slice = this.investment.slices[0];

      if (!slice) {
        return;
      }

      const budget = this.iVWizardService.getState('BUDGET_ALLOCATION');

      if (!budget) {
        return;
      }

      if (this.investment.id && (!budget.price || budget.price === 0)) {
        this.showDeleteInvestmentDialogFromBudget();
        return;
      }

      let statusId: number;
      if (budget.status) {
        statusId = typeof budget.status === 'object' && budget.status?.id
          ? budget.status.id
          : (typeof budget.status === 'number' ? budget.status : slice.status);
      } else {
        statusId = slice.status || this.investment.status;
      }
      const currentStatusId = this.investment.status_object?.id ?? this.investment.status;

      const slicePayload = {
        id: slice.id,
        year: budget.final_schedule_to,
        price: budget.price,
        additional_price: slice.additional_price || 0,
        add_price_reason: slice.add_price_reason || null,
        status: statusId,
        comments: slice.comments || '',
        is_extra_work: slice.is_extra_work || false,
      };

      const priorityId = budget.priority
        ? (typeof budget.priority === 'object' && budget.priority?.id ? budget.priority.id : budget.priority)
        : undefined;

      const budgetOriginId = budget.budget_origin
        ? (typeof budget.budget_origin === 'object' && budget.budget_origin?.id ? budget.budget_origin.id : budget.budget_origin)
        : undefined;

      const budgetScopeId = typeof budget.budget_scopes === 'number'
        ? budget.budget_scopes
        : budget.budget_scopes?.id ?? null;

      const payload: any = {
        slices: [slicePayload],
        price_sheet: budget.price_sheet_id,
      };

      if (statusId !== currentStatusId) {
        payload.status = statusId;
      }

      if (budgetScopeId !== (this.investment.budget_scopes?.[0] ?? null)) {
        payload.budget_scopes = budgetScopeId ? [budgetScopeId] : [];
      }

      if (priorityId !== undefined && priorityId !== null) {
        payload.priority = priorityId;
      }

      if (budgetOriginId !== undefined && budgetOriginId !== null) {
        payload.budget_origin = budgetOriginId;
      }

      if (this.investment.id) {
        this.invService.updateInvestment(this.investment.id, payload).pipe(
          tap(() => {
            this.saveToast.showSaved();
          }),
          switchMap(() => {
            const currentPrice = budget.price;
            if (!currentPrice || currentPrice === 0) {
              return of(null);
            }
            return this.invService.getInvestment(this.investment.id);
          }),
        ).subscribe({
          next: (updatedInvestment) => {
            if (updatedInvestment) {
              this.investment = updatedInvestment;
              this.lastSavedInvestment = updatedInvestment;
            }
            if (this.budgetAllocationComponent) {
              this.readOnly = this.shouldBudgetFormBeReadOnly(updatedInvestment);
              this.budgetAllocationComponent.setReadOnlyMode(this.readOnly);
              this.budgetAllocationComponent.refreshForm();
            }
          },
          error: (error) => {
            console.error('Error updating investment:', error);
            this.saveToast.showError('Failed to update investment');
          },
        });
      } else {
        this.ref.close(slicePayload);
      }
    } else if (this.type === 'GAIN_ENERGY_CARBON') {
      const gainsState = this.iVWizardService.getState('GAIN_ENERGY_CARBON'),
        perimeters = this.investment.perimeters,
        monoPerimeter = perimeters.find(p => p.is_monosite && p.building_id === this.investment.building);

      if (gainsState.enabled === false) {
        if (this.investment.initiative?.id) {
          this.iniService.deleteInitiative(this.investment.initiative.id).pipe(
            tap(() => {
              this.saveToast.showSaved();
            }),
            switchMap(() => {
              if (this.investment.id === 0) {
                return of(null);
              }
              const currentPrice = this.investment.slices?.[0]?.price;
              if (!currentPrice || currentPrice === 0) {
                return of(null);
              }
              return this.invService.getInvestment(this.investment.id);
            }),
          ).subscribe({
            next: (updatedInvestment) => {
              if (this.gainEnergyCarbonComponent) {
                this.gainEnergyCarbonComponent.refreshForm();
              }
              this.ref.close(updatedInvestment);
            },
            error: (error) => {
              console.error('Error deleting initiative:', error);
              this.saveToast.showError('Failed to remove initiative');
            },
          });
        } else {
          this.ref.close();
        }
      } else if (this.investment.initiative?.id) {
        const allGains = this.gainEnergyCarbonComponent
          ? (this.gainEnergyCarbonComponent.gains?.value || [])
          : (gainsState.gains || []);

        const isGainTypeNone = gainsState.initiative_type?.gain_type === 'None';
        const hadGainsBefore = (this.gainEnergyCarbonComponent?.initialGainsCount ?? 0) > 0;
        const allGainsAreZero = allGains.length === 0 || allGains.every(gain => {
          if (!gain || typeof gain !== 'object') return true;
          const energySavings = gain.theoretical_savings_full_year || 0;
          const carbonSavings = gain.theoretical_greenhouse_gas_savings_full_year || 0;
          return energySavings === 0 && carbonSavings === 0;
        });

        if (allGainsAreZero && !isGainTypeNone && hadGainsBefore) {
          this.showDeleteInitiativeDialogFromGains();
          return;
        }

        const filteredGains = this.gainEnergyCarbonComponent
          ? this.gainEnergyCarbonComponent.getFilteredGains()
          : this.filterEmptyGains(gainsState.gains || []);

        const gainsWithInitiativeId = filteredGains.map(gain => ({
          ...gain,
          initiative_id: this.investment.initiative.id,
        }));

        const updatePayload: any = {
          gains: gainsWithInitiativeId,
        };

        if (gainsState.initiative_type) {
          updatePayload.initiative_type = gainsState.initiative_type.id;
        }

        if (gainsState.responsible !== null && gainsState.responsible !== undefined) {
          updatePayload.responsible = gainsState.responsible;
        }
        if (gainsState.description !== null && gainsState.description !== undefined) {
          updatePayload.description = gainsState.description;
        } else {
          updatePayload.description = '';
        }
        if (gainsState.initially_proposed_by !== null && gainsState.initially_proposed_by !== undefined) {
          updatePayload.initially_proposed_by = gainsState.initially_proposed_by;
        }

        this.iniService.updateInitiative(this.investment.initiative.id, updatePayload).pipe(
          tap(() => {
            this.saveToast.showSaved();
          }),
          // switchMap(() => this.invService.getInvestment(this.investment.id, true)),
        ).subscribe({
          next: (initiative) => {
            if (this.gainEnergyCarbonComponent) {
              this.gainEnergyCarbonComponent.refreshForm();
            }
            this.ref.close({ initiative });
          },
          error: (error) => {
            console.error('Error updating initiative:', error);
            this.saveToast.showError('Failed to update initiative');
          },
        });
      } else {
        const allGains = this.gainEnergyCarbonComponent
          ? (this.gainEnergyCarbonComponent.gains?.value || [])
          : (gainsState.gains || []);

        const isGainTypeNone = gainsState.initiative_type?.gain_type === 'None';
        const hadGainsBefore = (this.gainEnergyCarbonComponent?.initialGainsCount ?? 0) > 0;
        const allGainsAreZero = allGains.length === 0 || allGains.every(gain => {
          if (!gain || typeof gain !== 'object') return true;
          const energySavings = gain.theoretical_savings_full_year || 0;
          const carbonSavings = gain.theoretical_greenhouse_gas_savings_full_year || 0;
          return energySavings === 0 && carbonSavings === 0;
        });

        if (allGainsAreZero && !isGainTypeNone && hadGainsBefore) {
          this.ref.close();
          return;
        }

        const filteredGains = this.gainEnergyCarbonComponent
          ? this.gainEnergyCarbonComponent.getFilteredGains()
          : this.filterEmptyGains(gainsState.gains || []);

        const updatedState = {
          ...gainsState,
          gains: filteredGains,
        };
        this.iVWizardService.setState('GAIN_ENERGY_CARBON', updatedState);

        this.iVWizardService.createInitiative(this.investment, monoPerimeter?.id).pipe(
          tap(() => {
            this.saveToast.showSaved();
          })
        ).subscribe((initiative) => {
          if (this.gainEnergyCarbonComponent) {
            this.gainEnergyCarbonComponent.refreshForm();
          }
          this.ref.close({ initiative });
        });
      }
    }
  }

  private showDeleteInvestmentDialogFromBudget() {
    const content = this.translate.instant('By deleting the price, you will delete all the investment\'s data');
    const dialogRef = this.dialogService.open(
      BasicDialogComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Delete investment'),
          content: content,
        },
      },
    );

    dialogRef.onClose.pipe(
      filter(confirm => !!confirm),
      tap(() => {
        if (this.budgetAllocationComponent) {
          this.budgetAllocationComponent.budgetForm.patchValue({ price: 0 });
          this.budgetAllocationComponent.budgetForm.disable();
        }
      }),
      switchMap(() => {
        return this.invService.deleteInvestment(this.investment.id);
      }),
    ).subscribe({
      next: () => {
        this.saveToast.showSaved();
        this.chartsService.capexHasChanged();
        this.ref.close({ deleted: true });
      },
      error: (error) => {
        console.error('Error deleting investment:', error);
        this.saveToast.showError('Failed to delete investment');
        if (this.budgetAllocationComponent) {
          this.budgetAllocationComponent.budgetForm.enable();
        }
      },
    });
  }

  private shouldBudgetFormBeReadOnly(investment = this.investment): boolean {
    const status = investment?.status_object;
    return this.initialReadOnly || !!(status?.is_closed || status?.is_validated || status?.is_locked);
  }

  private filterEmptyGains(gains: any[]): any[] {
    if (!gains || !Array.isArray(gains)) {
      return [];
    }

    return gains.filter(gain => {
      if (!gain || typeof gain !== 'object') {
        return false;
      }

      const hasData = Object.keys(gain).some(key => {
        const value = gain[key];
        return value !== null && value !== undefined && value !== '' && value !== false;
      });

      return hasData;
    });
  }

  private showDeleteInitiativeDialogFromGains() {
    const content = this.translate.instant('By deleting all gains, you will delete the action');
    const dialogRef = this.dialogService.open(
      BasicDialogComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Delete action'),
          content: content,
        },
      },
    );

    dialogRef.onClose.pipe(
      filter(confirm => !!confirm),
      switchMap(() => {
        return this.iniService.deleteInitiative(this.investment.initiative.id);
      }),
    ).subscribe({
      next: () => {
        this.saveToast.showSaved();
        this.ref.close({ deleted: true });
      },
      error: (error) => {
        console.error('Error deleting initiative:', error);
        this.saveToast.showError('Failed to delete action');
      },
    });
  }

  private initializeBackdropClick(): void {
    this.subscriptions.push(
      this.ref.onBackdropClick.subscribe(() => {
        if (this.documentsService.childDialogOpen) return;
        this.confirmClose();
      })
    )
  }

  protected confirmClose(): void {
    if (this.shouldShowSaveButton() && !this.readOnly) {
      this.dialogService.open(
        SideFormDialogComponent,
        {
          autoFocus: false,
          closeOnBackdropClick: false,
          context: {
            title: this.translate.instant('Unregistered changes'),
            content: this.translate.instant('Would you like to exit ?'),
          },
        }
      ).onClose.subscribe((confirmation: boolean) => {
        if (confirmation) {
          this.close();
        } else {
          return;
        }
      });
    } else {
      this.close();
    }
  }

  ngOnDestroy() {
    this.iVWizardService.investmentId = null;
    // TODO: reset the service because some data is kept when you close this page and you see it in the wizard.
    // currently not working
    this.iVWizardService.onDestroy();
  }

}

@Component({
  selector: 'app-investment-form',
  templateUrl: './investment-form.component.html',
  styleUrls: ['../base-form/base-form.component.scss', './investment-form-wrapper.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NgClass,
    NbSpinnerModule,
    NbButtonModule,
    NbTooltipModule,
    FontAwesomeModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    AsyncPipe,
    TranslateModule,
    NbTabsetModule,
    CommonModule,
    IntegerOnlyDirective,
    EntityDocumentsTabComponent,
    NbPopoverModule,
  ],
})
export class InvestmentFormComponent extends BaseFormComponent implements OnInit, OnDestroy {
  private formWasSaved: boolean = false;
  @Input() investment: KPI.MappedInvestment;
  @Input() initiative: boolean = false;
  @Input() initiativeId: number | null = null;
  @Input() inWizard: boolean = false;
  @Output() sendFormEvent: EventEmitter<any> = new EventEmitter<any>();

  // dependencies
  #iniService = inject(InitiativesService);
  public i18n = inject(I18nService);
  private ivWizardService = inject(InvestmentWizardService);
  private ivCatService = inject(InvestmentCategoriesService);
  private destroyRef = inject(DestroyRef);
  private assetsService = inject(AssetsService);
  private cdr = inject(ChangeDetectorRef);
  private documentsService = inject(DocumentsService);
  private readonly sideFormLevelService = inject(RightPanelDialogService);

  public title: string;
  public investmentSiteName: string = "";
  public showDocumentsPanel = false;
  public docCount = 0;
  public activeFormTab: 'description' | 'documents' = 'description';
  public openLinkInvestmentsList(): void {
    this.perimetersService.allPerimeters$.pipe(first()).subscribe((allPerimeters) => {
      let sitePerimeter: KPI.Perimeter = allPerimeters.find((p) => p.id === this.investment.multi_perimeter?.id);

      if (!sitePerimeter) {
          // Flatten and find by ID or building ID
          const flatPerimeters = allPerimeters.reduce((acc, p) => acc.concat([p, ...(p.sub_perimeters || [])]), [] as KPI.Perimeter[]);
          sitePerimeter = flatPerimeters.find(p => p.id === this.investment.multi_perimeter?.id || p.id === this.investment.building);
      }

      // If still not found, try to find any site containing the current building
      if (!sitePerimeter && this.investment.building) {
          sitePerimeter = allPerimeters.find(p => p.sub_perimeters?.some(sp => sp.id === this.investment.building));
      }

      const linkedToId = (this.investment as any).linked_to;

      const dialog = this.sideFormLevelService.open(LinkableInvestmentsListComponent, {
        autoFocus: false,
        context: {
          mainInvestmentId: this.investment.id,
          linkedToId: linkedToId || null,
          perimeter: sitePerimeter,
          startYear: 1950,
          endYear: 2100,
          inputLinkedElementIds: (this.investment as any).links_from?.map(l => l.id) || [],
          readOnly: false,
          multipleChoice: false,
        },
      });

      dialog.onClose.subscribe((result) => {
        if (result === 'new') {
          // disabled for now
          this.openCreateNewWizard();
        } else {
          this.investmentsService.reloadInvestment$.next(this.investment.id);
          this.chartsService.capexHasChanged();
        }
      });
    });
  }

  // Method handleChoice removed as no longer needed for drawer version

  private openCreateNewWizard(): void {
    forkJoin([
      this.scopeService.investmentTypes$.pipe(first()),
      this.scopeService.budgetScopes$.pipe(first()),
      this.scopeService.investmentStatuses$.pipe(first()),
      this.scopeService.investmentBudgetOrigins$.pipe(first()),
      this.scopeService.investmentPriorities$.pipe(first()),
      this.investmentReasonsService.investmentReasons$.pipe(first()),
      this.roadmapService.roadmapIndicators().pipe(first()),
      this.scopeService.getInvestFurtherInfo$.pipe(first()),
    ]).subscribe(([types, budgetScopes, statuses, origins, priorities, reasons, impacts, furtherInfos]) => {
      const typesArray = Object.values(types) as KPI.InvestmentType[];
      const mainType = typesArray.find(t => t.id === this.investment.investment_type);
      const otherType = typesArray.find(t =>
        ['other', 'autre', 'autres'].includes(t.name.toLowerCase())
      ) || typesArray[0];

      const budgetScope = budgetScopes[this.investment.budget_scopes[0]] || Object.values(budgetScopes)[0];
      const priority = priorities[this.investment.priority as any] || Object.values(priorities)[0];
      const budgetOrigin = origins[this.investment.budget_origin as any] || Object.values(origins)[0];
      const status = statuses[this.investment.status] || Object.values(statuses)[0];

      const reasonsArray = reasons as KPI.InvestmentReason[];
      const reasonsMap = new Map(reasonsArray.map(r => [r.id, r]));
      const selectedReasons = this.investment.reasons.map(id => reasonsMap.get(id)).filter(r => !!r);

      const impactsArray = impacts as KPI.RoadmapIndicator[];
      const selectedImpacts = impactsArray.filter(i => this.investment.impact_roadmap_indicators.includes(i.id));
      const selectedFurtherInfo = furtherInfos[this.investment.further_information as any];

      const monoPerimeter = this.selectedPerimeter; // Building perimeter
      const site = this.investment.perimeters?.find(p => !p.is_monosite) || monoPerimeter; // Top-level site
      const asset = (this.investment as any).asset_object;

      const nomenclatureTechnique: NomenclatureTechniqueStepState = {
        super_category: (this.investment as any).super_category_object,
        category: (this.investment as any).category_object,
        sub_category: (this.investment as any).sub_category_object,
      };

      const state: any = {
        'SITE_SELECTION': site,
        'INVESTMENT_TYPE_SELECTION': mainType || otherType,
        'MONO_PERIMETER_SELECTION': monoPerimeter,
        'ASSET_SELECTION': asset,
        'BUDGET_ALLOCATION': {
          price: null,
          status: status,
          final_schedule_to: null,
          budget_origin: budgetOrigin,
          budget_scopes: budgetScope,
          is_capex: this.investment.is_capex,
          aligned_taxonomy: false,
          priority: priority,
          price_sheet_id: this.investment.price_sheet,
        },
        'INVESTMENT_REASON_SELECTION': selectedReasons,
        'INVESTMENT_IMPACT_SELECTION': selectedImpacts,
        'INVESTMENT_FURTHER_INFORMATION_SELECTION': selectedFurtherInfo,
        'NOMENCLATURE_TECHNIQUE_SELECTION': nomenclatureTechnique,
        'LINKED_TO': this.investment.id,
        'INVESTMENT_SUBMIT_STEP': {
          label: null,
        },
      };

      this.ivWizardService.prefill(state, 'INVESTMENT_SUBMIT_STEP');

      this.sideFormLevelService.open(InvestmentWizardRootComponent, {
        autoFocus: true,
        closeOnBackdropClick: false,
        closeOnEsc: false,
      }).onClose.subscribe((result) => {
        if (result) {
          this.refreshInvestment();
          this.chartsService.capexHasChanged();
        }
      });
    });
  }

  public perimeters$ = this.perimetersService.perimeters$.pipe(
    first(),
    map(perimeters => {
      const investmentSite: KPI.Perimeter = this.investment.perimeters.find(perimeter => perimeter.building_id === null);
      const site = perimeters.find(p => p.id === investmentSite.id);
      const allPerimeters = [];
      this.investment.perimeters.forEach(perimeter => {
        allPerimeters.push({
          ...perimeter,
          parent: site,
        });
      });
      if (this.investment.asset_object?.perimeters) {
        this.investment.asset_object.perimeters.forEach(perimeter => {
          allPerimeters.push({
            ...perimeter,
            parent: site,
          });
        });
      }
      if (site?.sub_perimeters) {
        site.sub_perimeters.forEach(perimeter => {
          allPerimeters.push({
            ...perimeter,
            parent: site,
          });
        });
      }
      const uniquePerimeters = allPerimeters.filter(
        (perimeter, index, self) => index === self.findIndex(p => p.id === perimeter.id),
      );

      return uniquePerimeters.map(perimeter => {
        if (perimeter.level_parent) {
          const parentPerimeter = uniquePerimeters.find(p => p.id === perimeter.level_parent);
          if (parentPerimeter) {
            return {
              ...perimeter,
              parentPerimeter: parentPerimeter,
            };
          }
        }
        return perimeter;
      });
    }),
    toLoadingState<KPI.Perimeter[]>(),
  );
  public ivCategories = toSignal(this.ivCatService.investmentCategories$.pipe(takeUntilDestroyed()));
  public statuses$: Observable<LoadingState<KPI.InvestmentStatus[]>>;
  public selectedPerimeterLabel: string = '';
  public selectedInvestmentTypeLabel: string = '';
  public investmentTypes$ = this.scopeService.investmentTypes$.pipe(
    idMapToArray(),
    toLoadingState<KPI.InvestmentType[]>(),
  );
  public priorities$ = this.scopeService.investmentPriorities$.pipe(
    idMapToArray(),
    toLoadingState<KPI.InvestmentPriority[]>(),
  );
  public impacts$ = this.roadmapService.roadmapIndicators().pipe(toLoadingState<KPI.RoadmapIndicator[]>());
  public selectedBudgetScopeId: number = null;
  public furtherInformations$ = this.scopeService.getInvestFurtherInfo$.pipe(
    idMapToArray(),
    toLoadingState<KPI.FurtherInformation[]>(),
  );
  public taxonomyObjectives$ = this.scopeService.taxonomyObjectives$.pipe(
    idMapToArray(),
    toLoadingState<KPI.TaxonomyObjective[]>(),
  );
  public taxonomyCategories$ = this.scopeService.taxonomyCategories$.pipe(
    idMapToArray(),
    toLoadingState<KPI.TaxonomyCategory[]>(),
  );
  public reasons$: Observable<LoadingState<KPI.InvestmentReason[]>>;
  public years: number[];
  public isProposedLockedByWorkflow: boolean = false;
  private remainingFlashBudget$: Observable<number>;
  private hasPlannedProject: boolean;
  public selectedTabId: number = 1;
  public investmentCodeFieldName: string;
  public customerConfig$ = this.configService.customerConfig$;
  public statusValid: boolean;
  public canGoToAudit: boolean;
  public canEditQuotePart = toSignal(
    this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_QUOTE_PART]),
    { initialValue: false },
  );
  public isLocked: boolean;
  private customerConfig: KPI.CustomerConfig;
  budgetOrigins!: KPI.InvestmentBudgetOrigin[];
  budgetScopes!: KPI.BudgetScope[];
  #iniTypes = toSignal(this.#iniService.initiativeTypes$, { initialValue: [] });
  private statusesMap: Map<number, string> = new Map();
  allowedStatuses: KPI.InvestmentStatus[] = [];
  private prioritiesMap: Map<number, string> = new Map();
  private impactsMap: Map<number, string> = new Map();
  private reasonsMap: Map<number, string> = new Map();
  private furtherInfosMap: Map<number, string> = new Map();
  private technicalStateQuestionItemIds: Set<number> = new Set();
  public selectedPerimeter: KPI.Perimeter = null;
  public nextAssetObject: KPI.MappedAsset | null = null;
  energyModuleEnabled = this.configService.getSettingValue('ENERGY__ENABLE', false);
  // Energy-only fields shown on the initiative budget sheet, saved on the linked initiative (not the investment)
  public ceeBonus: number | null = null;
  public yearlyCostSavings: number | null = null;
  public energyFieldsInitiativeId: number | null = null;
  // Fields that must not be saved automatically. We want to show the save button for these.
  private showSaveBtnForTheseFields = [
    'status', 'price', 'project_code'
  ];
  // Text fields or number fields that we want to autoSave, but not each time the user presses a key
  private manualInputFields = [
    'label', 'comments'
  ];
  // Not the cleanest way to indicate pending changes, but we need to differentiate the changes that can
  // be saved autmatically and those that need the save button. So we can't just use the form.dirty property
  private hasPendingChanges: boolean = false;
  private saveToast = inject(SaveToastService);

  constructor(
    protected nbDialogRef: NbDialogRef<BaseFormComponent>,
    protected dialogService: NbDialogService,
    protected translate: TranslateService,
    private investmentsService: InvestmentsService,
    private scopeService: ScopeService,
    private perimetersService: PerimetersService,
    private roadmapService: RoadmapService,
    private investmentReasonsService: InvestmentReasonsService,
    private investmentsChanges: InvestmentsChangesService,
    private investmentStatusesService: InvestmentStatusesService,
    private apiService: APIService,
    private configService: ConfigService,
    private chartsService: ChartsService,
    private i18nService: I18nService,
    private usersService: UsersService,
    private stateLevelsService: StateLevelsService,
    private quotePartService: QuotePartService,

    // private wizardService: InvestmentWizardService,
  ) {
    super(nbDialogRef, dialogService, translate);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    const investmentSite: KPI.Perimeter = this.investment.multi_perimeter ||
      this.investment.perimeters?.find(perimeter => !perimeter.is_monosite);
    this.investmentSiteName = investmentSite?.name ?? "";
    this.subscriptions.push(
      this.scopeService.investmentSponsors$.subscribe(sponsorsMap => {
        this.sponsorsMap = sponsorsMap || {};
      }),
    );

    this.subscriptions.push(
      this.scopeService.auditQuestions$.pipe(idMapToArray()).subscribe(auditQuestions => {
        this.technicalStateQuestionItemIds = new Set(
          auditQuestions
            .filter(q => q.mnemonic === 'technical_state')
            .flatMap(q => q.items.map(item => item.id)),
        );
        setTimeout(() => {
          // This recomputes a display-only field once the (async-loaded) audit
          // question ids are available. It must also update initialValues,
          // otherwise it looks like an unsaved user change on close even
          // though nothing was actually edited.
          const formattedNotation = this.getFormattedInvestmentNotation();
          this.initialValues.investment_notation = formattedNotation;
          this.form?.get('investment_notation')?.setValue(formattedNotation, { emitEvent: false });
        });
      }),
    );

    this.subscriptions.push(
      this.quotePartService.quoteparts$.subscribe(quoteParts => {
        this.quotePartsMap = {};
        quoteParts.forEach(qp => {
          this.quotePartsMap[qp.id] = qp;
        });
      }),
    );
    this.quotePartService.getQuoteParts();

    this.subscriptions.push(
      combineLatest([
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_ACCESS_AUDIT]),
        this.configService.customerConfig$,
        this.scopeService.budgetScopes$.pipe(
          idMapToArray(),
          map(scopes => scopes.sort((a, b) => a.order - b.order)),
          first(),
        ),
        this.scopeService.investmentBudgetOrigins$.pipe(
          idMapToArray(),
          first(),
        ),
      ]).subscribe(([accessAllowed, config, budgetScopes, budgetOrigins]) => {
        this.canGoToAudit = accessAllowed && config.can_access_audit_app;
        this.budgetScopes = budgetScopes;
        this.budgetOrigins = budgetOrigins;
        this.customerConfig = config;
      }),
    );

    if (!this.inWizard) {
      const site: KPI.Perimeter = this.investment.multi_perimeter ||
        this.investment.perimeters.find(perimeter => !perimeter.is_monosite);
      if (site) {
        this.subscriptions.push(
          this.usersService.currentUserHasReadOnlyForSomePerimeters([site.id]).subscribe(
            permissions => {
              this.readOnly = permissions?.length > 0;
              this.isLocked = this.investment.status_object?.is_locked || this.readOnly;
            },
          ),
        );
      }
      if (this.investment.next_asset) {
        this.subscriptions.push(
          this.assetsService.getAsset(this.investment.next_asset).subscribe(
            nextAsset => {
              this.nextAssetObject = nextAsset;
              this.ivWizardService.setState('NEW_ASSET_STEP', {
                investmentType: this.investment.investment_type_object,
                value: {
                  asset_type: nextAsset.asset_type_object
                }
              });
            }
          )
        )
      }
    }

    this.hasPlannedProject = this.investment.projects?.some(project =>
      project.instances?.find(i => i.scenario?.id === this.investment.scenario && i.status?.is_request),
    );

    this.selectedPerimeter = this.investment.perimeters.find(p => p.building_id !== null);

    // When in initiative/budget mode, use the status-matched slice for year/price so the
    // displayed value is consistent with initiative.budget (which comes from current_price).
    const activeSlice = this.investment.slices.find(s => s.status === this.investment.status) ?? this.investment.slices[0];
    const rawBudgetPrice = this.initiative
      ? (this.investment.current_price ?? activeSlice?.price ?? this.investment.slices[0]?.price)
      : this.investment.slices[0]?.price;
    const budgetPrice = (this.initiative && rawBudgetPrice != null) ? Math.round(Number(rawBudgetPrice)) : rawBudgetPrice;
    const budgetYear = this.initiative
      ? (activeSlice?.year ?? this.investment.slices[0]?.year ?? this.investment.initial_schedule)
      : this.investment.slices[0]?.year;

    // Energy-only fields (cee_bonus / yearly_cost_savings) live on the initiative, not the investment.
    if (this.initiative && this.energyModuleEnabled && this.initiativeId) {
      this.energyFieldsInitiativeId = this.initiativeId;
      this.subscriptions.push(
        this.#iniService.getInitiative(this.energyFieldsInitiativeId).subscribe(ini => {
          this.ceeBonus = ini.cee_bonus ?? 0;
          this.yearlyCostSavings = ini.yearly_cost_savings ?? 0;
        }),
      );
    }

    this.initialValues = {
      label: this.investment.label,
      perimeter: this.selectedPerimeter?.id,
      investment_type: this.investment.investment_type,
      status: this.investment.status,
      budget_origin: this.investment.budget_origin?.id,
      year: budgetYear,
      price: budgetPrice,
      budget_scopes: this.investment.budget_scopes,
      impact_roadmap_indicators: this.investment.impact_roadmap_indicators,
      reasons: this.investment.reasons?.[0] ?? null,
      priority: this.investment.priority,
      further_information: this.investment.further_information?.id,
      comments: this.investment.comments,
      reference: this.investment.reference,
      custom_code: this.investment.custom_code,
      gtp_code: this.investment.gtp_code,
      project_code: this.investment.project_code,
      investment_notation: this.getFormattedInvestmentNotation(),
      aligned_taxonomy: (this.investment.taxonomy_objective || this.investment.taxonomy_category) ? this.translate.instant('Yes') : this.translate.instant('No'),
      capex: this.investment.is_capex ? this.translate.instant('Yes') : this.translate.instant('No'),
      technical_category: this.getTechnicalCategoryDisplay(),
      linked_equipment: this.getLinkedEquipmentDisplay(),
      distribution_key: this.getDistributionKeyDisplay(),
      linked_investment: this.getLinkedInvestmentsDisplay(),
      gain: this.getGainDisplay(),
      refacturation_preneurs: this.getRefacturationPreneursDisplay(),
      has_invoicing: (this.investment as any).has_invoicing ?? null,
      investment_category: this.investment.investment_category,
    };
    // Angular's FormControl/patchValue normalize an unset value to null (e.g. a
    // control built with an undefined formState falls back to its default,
    // which is null), while the fields above can end up as plain `undefined`
    // (optional chaining on missing data, e.g. perimeter/price when nothing is
    // set yet). Align the snapshot with that so anyFieldValueChanged() doesn't
    // mistake "still unset" for "changed".
    Object.keys(this.initialValues).forEach((key) => {
      if (this.initialValues[key] === undefined) {
        this.initialValues[key] = null;
      }
    });

    const isProposed = this.investment.status_object?.is_proposed;
    const isClosed = this.investment.status_object?.is_closed;
    const isValidated = this.investment.status_object?.is_validated;
    let isLocked = this.investment.status_object?.is_locked || this.readOnly;
    this.selectedBudgetScopeId = this.investment.budget_scopes[0];

    this.form = new UntypedFormGroup({
      label: new FormControl(
        this.initialValues.label,
        [Validators.required, Validators.maxLength(255)],
      ),
      perimeter: new UntypedFormControl(this.initialValues.perimeter),
      investment_type: new UntypedFormControl(
        { value: this.initialValues.investment_type, disabled: true },
        [Validators.required],
      ),
      status: new FormControl(
        this.initialValues.status,
        [Validators.required],
      ),
      price: new UntypedFormControl(
        { value: budgetPrice, disabled: isValidated || isProposed || this.isLocked },
        (this.initiative ? [] : [Validators.required]),
      ),
      budget_origin: new UntypedFormControl(
        { value: this.initialValues.budget_origin, disabled: isValidated || isProposed || this.isLocked },
        (this.initiative ? [] : [Validators.required]),
      ),
      year: new UntypedFormControl(
        { value: this.initialValues.year, disabled: isValidated || isProposed || this.isLocked },
        [Validators.required],
      ),
      budget_scopes: new UntypedFormControl(
        { value: this.initialValues.budget_scopes, disabled: isValidated || isProposed || this.isLocked },
      ),
      impact_roadmap_indicators: new UntypedFormControl(
        { value: this.initialValues.impact_roadmap_indicators, disabled: isValidated || isProposed || this.isLocked },
      ),
      reasons: new UntypedFormControl(
        { value: this.initialValues.reasons, disabled: isValidated || isProposed || this.isLocked },
      ),
      priority: new UntypedFormControl(
        { value: this.initialValues.priority, disabled: isValidated || this.isLocked || isProposed },
        [Validators.required],
      ),
      further_information: new UntypedFormControl(
        { value: this.initialValues.further_information, disabled: isValidated || isProposed || this.isLocked },
      ),
      comments: new UntypedFormControl(
        { value: this.initialValues.comments, disabled: isProposed || this.isLocked },
      ),
      reference: new UntypedFormControl(
        { value: this.initialValues.reference, disabled: isValidated || isProposed || this.isLocked },
      ),
      custom_code: new UntypedFormControl(
        { value: this.initialValues.custom_code, disabled: this.isLocked },
      ),
      gtp_code: new UntypedFormControl(
        { value: this.initialValues.gtp_code, disabled: false },
      ),
      project_code: new UntypedFormControl(
        { value: this.initialValues.project_code, disabled: isValidated || isProposed || this.isLocked },
      ),
      investment_notation: new UntypedFormControl(
        { value: this.initialValues.investment_notation, disabled: true },
      ),
      aligned_taxonomy: new UntypedFormControl(
        this.initialValues.aligned_taxonomy,
      ),
      capex: new UntypedFormControl(
        { value: this.initialValues.capex, disabled: this.isLocked },
      ),
      technical_category: new UntypedFormControl(
        { value: this.initialValues.technical_category, disabled: true },
      ),
      linked_equipment: new UntypedFormControl(
        { value: this.initialValues.linked_equipment, disabled: true },
      ),
      distribution_key: new UntypedFormControl(
        { value: this.initialValues.distribution_key, disabled: true },
      ),
      spending_category: new UntypedFormControl(
        { value: this.initialValues.spending_category, disabled: true },
      ),
      linked_investment: new UntypedFormControl(
        { value: this.initialValues.linked_investment, disabled: true },
      ),
      gain: new UntypedFormControl(
        { value: this.initialValues.gain, disabled: this.isLocked },
      ),
      refacturation_preneurs: new UntypedFormControl(
        { value: this.initialValues.refacturation_preneurs, disabled: true },
      ),
      has_invoicing: new UntypedFormControl(
        { value: this.initialValues.has_invoicing, disabled: isValidated || isProposed || isLocked },
      ),
      investment_category: new UntypedFormControl(
        { value: this.initialValues.investment_category, disabled: isValidated || isProposed || isLocked },
      ),
      price_sheet: new UntypedFormControl(
        { value: this.initialValues.price_sheet, disabled: isValidated || isProposed || isLocked }
      )
    });

    // Disable status if workflow is enabled and the investment is proposed
    this.configService.workflowEnabled$.pipe(first()).subscribe((workflowEnabled) => {
      if ((workflowEnabled && isProposed) || isValidated || isClosed || this.isLocked) {
        this.toggleDisableAllFields(true);
        if (isValidated) {
          if (!this.readOnly) {
            this.form.get('status').enable();
          }
        } else {
          this.isProposedLockedByWorkflow = true;
        }
      } else {
        this.toggleDisableAllFields(false);
      }
      if (!this.readOnly) {
        this.form.get('status')?.enable();
      }
    });

    if (this.investment.id == null ||
        this.investment.id === undefined ||
        !this.investment.multi_perimeter?.id ||
        !this.investment.final_schedule) {
      this.remainingFlashBudget$ = of(null);
    } else {
      this.remainingFlashBudget$ = this.apiService.get_remaining_flash_budget(
        this.investment.multi_perimeter.id,
        this.investment.final_schedule,
        this.investment.id,
      ).pipe(
        catchError((error) => {
          console.error('[InvestmentForm] Error fetching remaining flash budget:', error);
          return of(null);
        })
      );
    }

    this.form.patchValue({
      price: budgetPrice ?? null,
    });

    this.form.get('price').valueChanges.subscribe(() => {
      if (this.inWizard && this.initiative) {
        this.sendFormEvent.emit(this.form);
      }
    });

    this.isStatusValid(this.investment.status_object);

    // Set up available values
    this.setUpAvailableValues();

    this.setupPerimeterSelection();
    this.setupInvestmentTypeSelection();

    this.title = this.initiative ? this.translate.instant('Budget') : this.translate.instant('Investment-sheet');
    this.subscriptions.push(
      this.configService.customerConfig$.subscribe((config) => {
        if (config.investment_code_field_name) {
          this.investmentCodeFieldName = config.investment_code_field_name;
        } else {
          this.investmentCodeFieldName = this.translate.instant('Customize code');
        }
        this.investmentCodeFieldName += ` (${this.translate.instant('Optional').toLowerCase()})`;
      }),
    );
    if (this.inWizard && this.initiative) {
      this.subscriptions.push(
        this.form.valueChanges.subscribe(() => {
          this.sendFormEvent.emit(this.form);
        }),
      );
    } else {
      this.initializeBackdropClick();
    }

    // Refresh investment data when notified
    this.subscriptions.push(
      this.investmentsService.reloadInvestment$.pipe(
        filter(id => id === this.investment.id || id === 0),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.refreshInvestment();
      })
    );

    // Load document count
    this.loadDocumentCount();

    // Refresh document count when documents change
    this.subscriptions.push(
      this.documentsService._documentsHaveChanged$.subscribe(() => {
        this.loadDocumentCount();
      })
    );
  }

  public saveCeeBonus(): void {
    if (this.energyFieldsInitiativeId === null) return;
    this.#iniService.updateInitiative(this.energyFieldsInitiativeId, { cee_bonus: this.ceeBonus }).subscribe();
  }

  public saveYearlyCostSavings(): void {
    if (this.energyFieldsInitiativeId === null) return;
    this.#iniService.updateInitiative(this.energyFieldsInitiativeId, { yearly_cost_savings: this.yearlyCostSavings }).subscribe();
  }

  public loadDocumentCount(): void {
    this.subscriptions.push(
      this.documentsService.getDocumentsCount(
        [],
        [this.investment.id],
        [],
        []
      ).subscribe((count) => {
        this.docCount = count;
        this.cdr.detectChanges();
      })
    );
  }

  public toggleDocumentsPanel(): void {
    this.showDocumentsPanel = !this.showDocumentsPanel;
  }

  public onDocumentsDrawerClosed(): void {
    this.showDocumentsPanel = false;
    this.loadDocumentCount();
  }

  public onSubmit(): void {
    const price = this.form.get('price').value;
    const priceValue = price != null ? Number(price) : null;
    if (this.investment.id && (!priceValue || priceValue === 0)) {
      this.showDeleteInvestmentDialog();
    } else if (this.isFormValid()) {
      this.scopeService.investmentStatuses$.subscribe((_statuses) => {
        this.validateForm();
      });
    }
  }

  private validateForm(close: boolean = true) {
    this.loading = true;
    this.setSaving();

    if (this.investment.id == null || this.investment.id === undefined) {
      this.close();
      return;
    }

    const price = this.form.get('price').value;

    if (this.investment.id && (!price || price === 0)) {
      this.loading = false;
      this.showDeleteInvestmentDialog();
      return;
    }

    const sliceKeys: string[] = ['price', 'year'];
    const changes: { property: string; value: any }[] = Object.keys(this.initialValues)
      .filter(key => !sliceKeys.includes(key) && this.fieldValueChanged(key))
      .map((key: string) => {
        let property = key;
        let value = this.form.get(key).value;

        if (key === 'capex') {
          property = 'is_capex';
          value = value === this.translate.instant('Yes');
        }

        if (key === 'reasons') {
          value = value != null ? [value] : [];
        }

        return {
          property,
          value,
        };
      });

    // In initiative/budget mode, save to the status-matched slice so that investment.current_price
    // (= sum of slices matching investment.status) is recalculated correctly after the API call.
    const targetSlice = this.initiative
      ? (this.investment.slices.find(s => s.status === this.investment.status) ?? this.investment.slices[0])
      : this.investment.slices[0];
    const sliceChanges: KPI.InvestmentSlice = sliceKeys.some((key) => this.fieldValueChanged(key))
      ? sliceKeys.reduce((acc, key) => {
        acc[key] = this.form.get(key).value;
        return acc;
      }, {
        ...targetSlice,
        ...(this.fieldValueChanged('status') ? { status: this.form.get('status').value } : {}),
      } as KPI.InvestmentSlice)
      : null;

    if (sliceChanges) {
      changes.push({
        property: 'slices',
        value: [sliceChanges],
      });
    }

    if (this.investment.id === 0) {
      this.loading = false;
      this.formWasSaved = true;
      this.close();
      return;
    }

    const initialPrice = this.initialValues.price;
    if (!initialPrice || initialPrice === 0) {
      this.loading = false;
      this.formWasSaved = true;
      this.close();
      return;
    }

    this.investmentsChanges.addChanges(this.investment.id, changes);

      const mustRefreshCapexCharts = changes.some((change) =>
        ['slices', 'status', 'is_capex'].includes(change.property),
      );

      this.investmentsChanges.pendingChangesNumber$.pipe(
        filter((nb) => !nb),
        first(),
        switchMap(() => {
          if (this.investment.id === 0) {
            return of(null);
          }
          const currentPrice = this.form.get('price')?.value;
          if (!currentPrice || currentPrice === 0) {
            return of(null);
          }
          return this.investmentsService.getInvestment(this.investment.id).pipe(
            catchError(() => {
              return of(null);
            }),
          );
        }),
      ).subscribe((updatedInvestment) => {
        changes.map((change) => {
          this.investment[change.property] = change.value;
          if (change.property !== 'slices') {
            if (change.property === 'is_capex') {
              this.initialValues['capex'] = change.value ? this.translate.instant('Yes') : this.translate.instant('No');
            } else if (change.property === 'reasons') {
              // reasons is stored as array in API but as single ID in form control
              this.initialValues['reasons'] = (change.value as number[])?.[0] ?? null;
            } else {
              this.initialValues[change.property] = change.value;
            }
          }
        });

        if (updatedInvestment && 'asset_object' in updatedInvestment) {
          Object.assign(this.investment, updatedInvestment);
          const refreshedSlice = this.initiative
            ? (this.investment.slices.find(s => s.status === this.investment.status) ?? this.investment.slices[0])
            : this.investment.slices[0];
          this.initialValues.budget_origin = this.investment.budget_origin?.id;
          this.initialValues.year = refreshedSlice?.year;
          const refreshedPrice = this.initiative
            ? (this.investment.current_price ?? refreshedSlice?.price)
            : refreshedSlice?.price;
          this.initialValues.price = (this.initiative && refreshedPrice != null) ? Math.round(Number(refreshedPrice)) : refreshedPrice;
          this.initialValues.budget_scopes = this.investment.budget_scopes;
          this.initialValues.custom_code = this.investment.custom_code;
          this.initialValues.project_code = this.investment.project_code;
          this.selectedBudgetScopeId = this.investment.budget_scopes?.[0];
          this.form.patchValue({
            budget_origin: this.initialValues.budget_origin,
            year: this.initialValues.year,
            price: this.initialValues.price,
            budget_scopes: this.initialValues.budget_scopes,
            custom_code: this.initialValues.custom_code,
            project_code: this.initialValues.project_code,
          }, { emitEvent: false });
        } else {
          const updatedSliceKeys = ['year', 'price'];
          updatedSliceKeys.forEach(key => {
            if (sliceChanges?.hasOwnProperty(key)) {
              this.initialValues[key] = sliceChanges[key];
            }
          });
          this.form.patchValue({
            price: this.initialValues.price,
            year: this.initialValues.year,
          }, { emitEvent: false });
        }

        if (mustRefreshCapexCharts) {
          this.chartsService.capexHasChanged();
        }
        if (changes.some(change => change.property === 'status')) {
          this.refreshFormDisabledState();
        }
        this.loading = false;
        this.formWasSaved = true;
        this.hasPendingChanges = false;
        this.setSaved();
        if (close) {
          if (this.initiative) {
            this.nbDialogRef.close({ investment: this.investment });
          } else {
            this.close();
          }
        }
      });
  }

  /** Open an iframe to show the capex audit */
  public goToAudit() {
    this.investmentsService.openAuditInvestEditor({
      investmentId: this.investment.id,
      siteId: this.investment.multi_perimeter.id,
    });
  }

  protected setUpAvailableValues(): void {
    /**
     * STATUSES
     */
    this.statuses$ = combineLatest([
      this.investmentStatusesService.getAllowedTransitionsStatus(),
      this.scopeService.investmentStatuses$.pipe(idMapToArray()),
      combineLatest([this.configService.workflowEnabled$, this.remainingFlashBudget$]),
    ]).pipe(
      map(([allowedTransactions, allStatuses, [workflowEnabled, remainingFlashBudget]]) => {
        const statusId = typeof this.initialValues.status === 'object'
          ? (this.initialValues.status as any)?.id
          : this.initialValues.status;
        const fromStatus = allStatuses.find(status => status.id === statusId);
        if (!fromStatus) {
          return allStatuses;
        }
        const allowedStatusesIds = allowedTransactions
          .filter(t => t.from_status === fromStatus.id)
          .map(t => t.to_status);
        const budgetYear = this.investment.slices[0]?.year || this.investment.final_schedule;
        const currentYear = new Date().getFullYear();
        const shouldDisableClosed = budgetYear && budgetYear > currentYear;
        // Return current status + allowed statuses + check worlfow rules
        return allStatuses.filter((status) =>
            (status.id === fromStatus.id // Keep the current status
              || allowedStatusesIds.includes(status.id)) // Keep the allowed statuses
            && !(workflowEnabled && fromStatus.is_proposed && status.id !== fromStatus.id) // can't move a proposed investment if worlfow is enabled
            && !(
              workflowEnabled
              && status.is_validated
              && this.investment.priority_object?.is_flash_investment
              && this.form.get('price').value > remainingFlashBudget
            ) // Remove validated status if the investment is flash and the price is higher than the remaining flash budget
            && !(
              status.hypothesis && this.hasPlannedProject
            )
            && !(
              shouldDisableClosed && status.is_closed
            )
        );
      }),
      toLoadingState<KPI.InvestmentStatus[]>(),
    );

    if (this.inWizard && this.initiative && (!this.investment?.id || this.investment.id === 0)) {
      this.statuses$ = this.statuses$.pipe(
        map(statusesState => ({
          ...statusesState,
          data: statusesState.data?.filter(status => status.is_request) || []
        }))
      );
    }

    /**
     * REASONS (TAGS)
     */
    this.reasons$ = this.investmentReasonsService.investmentReasons$.pipe(
      map(reasons =>
        reasons.filter(
          reason =>
            !reason.only_for_investment_types?.length ||
            reason.only_for_investment_types.includes(this.investment.investment_type),
        ),
      ),
      toLoadingState<KPI.InvestmentReason[]>(),
    );

    this.subscriptions.push(
      this.statuses$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(statuses => {
        if (statuses?.data) {
          statuses.data.forEach((status: any) => {
            this.statusesMap.set(status.id, status.name);
          });
          this.allowedStatuses = statuses.data;
        }
      }),
      this.scopeService.investmentStatuses$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(statusesMap => {
        Object.values(statusesMap || {}).forEach((status: any) => {
          if (status?.id != null) this.statusesMap.set(status.id, status.name);
        });
      }),
      this.priorities$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(priorities => {
        if (priorities?.data) {
          priorities.data.forEach((priority: any) => {
            this.prioritiesMap.set(priority.id, priority.name);
          });
        }
      }),
      this.impacts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(impacts => {
        if (impacts?.data) {
          impacts.data.forEach((impact: any) => {
            this.impactsMap.set(impact.id, impact.name);
          });
        }
      }),
      this.reasons$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(reasons => {
        if (reasons?.data) {
          reasons.data.forEach((reason: any) => {
            this.reasonsMap.set(reason.id, reason.name);
          });
        }
      }),
      this.furtherInformations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(furtherInfos => {
        if (furtherInfos?.data) {
          furtherInfos.data.forEach((furtherInfo: any) => {
            this.furtherInfosMap.set(furtherInfo.id, furtherInfo.name);
          });
        }
      }),
    );

    /**
     * YEARS
     */
    this.years = this.getYears();
  }

  private getYears(): number[] {
    const startYear = Math.min(this.getStartYear(), new Date().getFullYear());
    const endYear = this.getEndYear();
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
  }

  private getStartYear(): number {
    const currentYear = new Date().getFullYear();
    const asset = this.investment.asset_object;
    if (this.investment?.final_schedule) {
      return this.investment.final_schedule;
    } else if (asset?.installation_year) {
      return Math.max(currentYear, asset.installation_year);
    }

    return currentYear;
  }

  private getEndYear(): number {
    return Math.max(this.getStartYear() + 20, this.investment.final_schedule_to);
  }



  public getPrice(): number {
    const priceValue = this.form?.get('price').value;
    return priceValue ? Number(priceValue) : 0;
  }

  public getFormattedBudgetAmount(): string {
    const amount = this.investment.slices[0]?.price || 0;
    if (amount === 0) return 'k€0';
    return this.i18nService.formatPriceWithThousands(Number(amount));
  }

  public getBudgetYear(): string {
    const year = this.investment.slices[0]?.year || this.investment.final_schedule;
    return year ? this.translate.instant('in {{year}}', { year }) : '';
  }

  public getBudgetOrigin(): string {
    return this.investment.budget_origin?.name || '';
  }

  public getTechnicalCategoryDisplay(): string {
    const parts = [];

    if (this.investment.super_category_object?.name) {
      parts.push(this.investment.super_category_object.name);
    }

    if (this.investment.category_object?.name) {
      parts.push(this.investment.category_object.name);
    }

    if (this.investment.sub_category_object?.name) {
      parts.push(this.investment.sub_category_object.name);
    }

    if (this.investment.asset_object?.asset_type_object?.name) {
      parts.push(this.investment.asset_object.asset_type_object.name);
    }

    if (parts.length === 0) {
      return '-';
    }

    return parts.join(' > ');
  }

  public getLinkedEquipmentDisplay(): string {
    if (this.investment.asset) return this.investment?.asset_object?.label;
    if (this.investment.next_asset) return this.nextAssetObject?.label || this.translate.instant('Future equipment');
    return '-';
  }

  public getDistributionKeyDisplay(): string {
    if (this.investment.quote_part?.name) {
      return this.investment.quote_part.name;
    }

    const quotePartId = this.investment.quote_part?.id || (this.investment as any).quote_part_id;

    if (quotePartId) {
      const quotePart = this.quotePartsMap[quotePartId];
      if (quotePart?.name) {
        return quotePart.name;
      }
      return quotePartId.toString();
    }

    return '-';
  }

  private sponsorsMap: { [id: number]: KPI.Sponsor } = {};
  private quotePartsMap: { [id: number]: QuotePartItem } = {};

  public getQuotePartDetailsDisplay(): string {
    if (!this.investment.sponsorships || this.investment.sponsorships.length === 0) {
      return '-';
    }

    return this.investment.sponsorships
      .map(sponsorship => {
        const sponsor = this.sponsorsMap[sponsorship.sponsor.id];
        const sponsorName = sponsor?.name || `Sponsor ${sponsorship.sponsor.id}`;
        return `${sponsorship.percentage}% ${sponsorName}`;
      })
      .join(', ');
  }

  public getLinkedInvestmentsDisplay(): string {
    const linkedInvestsTotal = (this.investment as any).linked_invests_total;
    if (linkedInvestsTotal !== undefined && linkedInvestsTotal !== null && linkedInvestsTotal > 0) {
      return linkedInvestsTotal.toString();
    }
    const linksFrom = (this.investment as any).links_from;
    if (linksFrom && Array.isArray(linksFrom) && linksFrom.length > 0) {
      return linksFrom.length.toString();
    }
    return '-';
  }

  public isMainInvestment(): boolean {
    // Main investment is one that doesn't have a linked_to value
    return !(this.investment as any).linked_to;
  }

  public refreshInvestment(): void {
    this.investmentsService.getInvestment(this.investment.id).subscribe((investment) => {
      if (investment) {
        this.investment = investment as any;
        this.cdr.detectChanges();
        this.selectedPerimeter = this.investment.perimeters.find(p => p.building_id !== null);
      }
    });
  }



  private readonly _gainUpdated = signal(0);

  public get shouldUseLockedFieldStyle(): boolean {
    return this.readOnly || this.isLocked || this.isProposedLockedByWorkflow;
  }
  getGainDisplay = computed(() => {
    this._gainUpdated();
    const action = this.investment.initiative,
      iType = this.#iniTypes().find(it => it.id === action?.initiative_type);
    if (!action || !action.initiative_type) return '-';
    if (iType?.gain_type === 'None') return this.translate.instant('No gains to be made on this type of action');
    if (iType?.gain_type === 'QUIT_FOSSIL') return this.translate.instant('Abandon energy fossil');
    const gains = action.gains as KPI.InitiativeGain[];
    if (!gains || gains.length === 0) return '-';

    if (iType?.gain_type === 'ENERGY') {
      const totalSavings = gains.reduce((sum, gain) => {
        const value = gain.theoretical_savings_full_year;
        if (value == null) return sum;
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
        return sum + (isFinite(numValue) ? numValue : 0);
      }, 0);
      if (totalSavings === 0 || !isFinite(totalSavings)) return '-';
      return this.translate.instant('{savings} kWh (expected gain)',
        { savings: formatNumber(totalSavings, this.i18n.getCulture()) },
      );
    } else if (iType?.gain_type === 'CARBON') {
      const totalSavings = gains.reduce((sum, gain) => {
        const value = gain.theoretical_greenhouse_gas_savings_full_year;
        if (value == null) return sum;
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
        return sum + (isFinite(numValue) ? numValue : 0);
      }, 0);
      if (totalSavings === 0 || !isFinite(totalSavings)) return '-';
      return this.translate.instant('{savings} kgCO2 (expected gain)',
        { savings: formatNumber(totalSavings, this.i18n.getCulture()) },
      );
    }
    return '-';
  });

  public getRefacturationPreneursDisplay(): string {
    const hasInvoicing = (this.investment as any).has_invoicing;
    if (hasInvoicing === true) {
      return this.translate.instant('Yes');
    } else if (hasInvoicing === false) {
      return this.translate.instant('No');
    } else {
      return this.translate.instant('To be defined');
    }
  }

  private toggleDisableAllFields(disabled: boolean): void {
    if (disabled) {
      this.form.get('label')?.disable({ emitEvent: false });
      if (this.readOnly) {
        this.form.get('status')?.disable({ emitEvent: false });
      }
      this.form.get('budget_scopes')?.disable({ emitEvent: false });
      this.form.get('priority')?.disable({ emitEvent: false });
      this.form.get('price')?.disable({ emitEvent: false });
      this.form.get('year')?.disable({ emitEvent: false });
      this.form.get('budget_origin')?.disable({ emitEvent: false });
      this.form.get('impact_roadmap_indicators')?.disable({ emitEvent: false });
      this.form.get('reasons')?.disable({ emitEvent: false });
      this.form.get('further_information')?.disable({ emitEvent: false });
      this.form.get('reference')?.disable({ emitEvent: false });
      this.form.get('project_code')?.disable({ emitEvent: false });
      this.form.get('investment_notation')?.disable({ emitEvent: false });
      this.form.get('has_invoicing')?.disable({ emitEvent: false });
    } else if (!this.readOnly) {
      this.form.get('label')?.enable({ emitEvent: false });
      this.form.get('status')?.enable({ emitEvent: false });
      this.form.get('budget_scopes')?.enable({ emitEvent: false });
      this.form.get('priority')?.enable({ emitEvent: false });
      this.form.get('price')?.enable({ emitEvent: false });
      this.form.get('year')?.enable({ emitEvent: false });
      this.form.get('budget_origin')?.enable({ emitEvent: false });
      this.form.get('impact_roadmap_indicators')?.enable({ emitEvent: false });
      this.form.get('reasons')?.enable({ emitEvent: false });
      this.form.get('further_information')?.enable({ emitEvent: false });
      this.form.get('reference')?.enable({ emitEvent: false });
      this.form.get('project_code')?.enable({ emitEvent: false });
      this.form.get('investment_notation')?.enable({ emitEvent: false });
      this.form.get('has_invoicing')?.enable({ emitEvent: false });
    }
    if (!this.readOnly) {
      this.form.get('status')?.enable({ emitEvent: false });
    }
  }

  public anyFieldValueChanged(): boolean {
    let allowAutoSave = true;
    const changedFields = Object.keys(this.initialValues).filter((key: string) => {
      if (this.manualInputFields.includes(key)) {
        // We don't want the autoSave function to be triggered for each key the user types
        // but we don't want to show the save button either
        return false;
      }
      const hasChanged = this.fieldValueChanged(key);
      if (hasChanged && this.showSaveBtnForTheseFields.includes(key)) {
        allowAutoSave = false;
      }
      return hasChanged;
    });

    if (changedFields.length > 0) {
      if (allowAutoSave && !this.initiative) {
        this.autoSave();
        return false;
      }
      this.hasPendingChanges = true;
      return true;
    } else {
      return false;
    }
  }

  public isFormValid(): boolean {
    if (!this.form) {
      return false;
    }
    const isFormValid = this.form.valid;

    if (!isFormValid) {
      const errors: any = {};
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control && control.invalid) {
          errors[key] = { errors: control.errors, value: control.value };
        }
      });
    }

    return isFormValid;
  }

  public isStatusValid(status: KPI.InvestmentStatus): void {
    this.statusValid = status && (status.is_closed || status.is_validated || status.is_engaged || status.is_pre_validated);
  }

  public close(): void {
    if (this.formWasSaved) {
      this.nbDialogRef.close(this.form);
    } else {
      this.customNbCardClass = 'slide-out';
      setTimeout(() => this.nbDialogRef.close(null), 150);
    }
  }

  public openInvestmentSheet(): void {
    this.dialogService.open(
      SheetDialogComponent, {
        autoFocus: false,
        hasBackdrop: true,
        hasScroll: true,
        context: {
          investmentId: this.investment.id,
          deleteFilter: false,
        },
      },
    );
  }

  private setupPerimeterSelection(): void {
    this.setSelectedPerimeterLabelAndArea(this.initialValues.perimeter);
    this.subscriptions.push(
      this.form.get('perimeter').valueChanges.subscribe(perimeterId => {
        this.setSelectedPerimeterLabelAndArea(perimeterId);
      }),
    );
  }

  private setSelectedPerimeterLabelAndArea(selectedPerimeterId): void {
    this.getSelectedSiteAndPerimeterFromId(selectedPerimeterId).subscribe(([_, perimeter]) => {
      this.selectedPerimeter = perimeter;
      this.selectedPerimeterLabel = perimeter ? this.getPerimeterDisplayName(perimeter) : '';
    });
  }

  private getSelectedSiteAndPerimeterFromId(perimeterId: number): Observable<[KPI.Perimeter, KPI.Perimeter]> {
    return this.perimeters$.pipe(
      map(perimetersState => {
        if (perimetersState.loading || !perimetersState.data) {
          return [null, null];
        }
        const sitePerimeters = perimetersState.data;
        const selectedPerimeter = sitePerimeters.find(perimeter => perimeter.id === perimeterId);
        if (selectedPerimeter) {
          const site: KPI.Perimeter = this.investment.perimeters.find(perimeter => perimeter.building_id === null);
          return [site, selectedPerimeter];
        }
        const currentPerimeter = this.investment.perimeters.find(p => p.id === perimeterId);
        if (currentPerimeter) {
          const site: KPI.Perimeter = this.investment.perimeters.find(perimeter => perimeter.building_id === null);
          return [site, currentPerimeter];
        }
        return [null, null];
      }),
    );
  }

  private setupInvestmentTypeSelection(): void {
    this.setSelectedInvestmentTypeLabel(this.initialValues.investment_type);
    this.subscriptions.push(
      this.form.get('investment_type').valueChanges.subscribe(investmentTypeId => {
        this.setSelectedInvestmentTypeLabel(investmentTypeId);
      }),
    );
  }

  private refreshFormDisabledState(): void {
    const statusId = this.form.get('status').value;
    if (!statusId) {
      return;
    }

    this.scopeService.investmentStatuses$.pipe(first()).subscribe(statuses => {
      const newStatus = statuses[statusId];
      if (newStatus) {
        this.investment.status_object = newStatus;

        const isProposed = newStatus.is_proposed;
        const isClosed = newStatus.is_closed;
        const isValidated = newStatus.is_validated;
        const isLocked = newStatus.is_locked || this.readOnly;
        this.isLocked = isLocked;

        this.configService.workflowEnabled$.pipe(first()).subscribe((workflowEnabled) => {
          if ((workflowEnabled && isProposed) || isValidated || isClosed || isLocked) {
            this.toggleDisableAllFields(true);
            if (isValidated) {
              this.isProposedLockedByWorkflow = false;
              if (!this.readOnly) {
                this.form.get('status').enable({ emitEvent: false });
              }
            } else {
              this.isProposedLockedByWorkflow = true;
            }
          } else {
            // Status is editable again (e.g. back to Requested): clear the
            // workflow lock flag so fields don't keep the locked/greyed style.
            this.isProposedLockedByWorkflow = false;
            this.toggleDisableAllFields(false);
          }
          if (!this.readOnly) {
            this.form.get('status')?.enable({ emitEvent: false });
          }

          this.updateFieldDisabledStates(isValidated, isProposed, isLocked);
        });

        this.isStatusValid(newStatus);
      }
    });
  }

  private updateFieldDisabledStates(isValidated: boolean, isProposed: boolean, isLocked: boolean): void {
    if (this.readOnly) {
      return;
    }

    const fieldGroups: Array<{
      fields: string[];
      shouldDisable: boolean;
    }> = [
      {
        fields: [
          'label', 'price', 'budget_origin', 'year', 'budget_scopes',
          'impact_roadmap_indicators', 'reasons', 'further_information', 'reference',
          'project_code'
        ],
        shouldDisable: isValidated || isProposed || isLocked,
      },
      {
        fields: ['priority', 'investment_category'],
        shouldDisable: isValidated || isLocked || isProposed,
      },
      {
        fields: ['comments'],
        shouldDisable: isProposed || isLocked,
      },
      {
        fields: ['custom_code', 'capex', 'gain'],
        shouldDisable: isLocked,
      },
    ];

    fieldGroups.forEach(({ fields, shouldDisable }) => {
      fields.forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          if (shouldDisable) {
            control.disable({ emitEvent: false });
          } else {
            control.enable({ emitEvent: false });
          }
        }
      });
    });
  }

  private setSelectedInvestmentTypeLabel(investmentTypeId: number): void {
    this.getInvestmentTypeById(investmentTypeId).subscribe(investmentType => {
      this.selectedInvestmentTypeLabel = investmentType ? investmentType.name : '';
    });
  }

  private getInvestmentTypeById(investmentTypeId: number): Observable<KPI.InvestmentType> {
    return this.scopeService.investmentTypes$.pipe(
      first(),
      map(investmentTypesMap => {
        return investmentTypesMap[investmentTypeId] || null;
      }),
    );
  }

  public getPerimeterDisplayName(perimeter: KPI.Perimeter & {
    parent?: KPI.Perimeter;
    parentPerimeter?: KPI.Perimeter
  }): string {
    if (!perimeter) return '';
    if (!perimeter.building_id) {
      return perimeter.name;
    }

    let siteName = '';
    if (this.investment.next_asset && this.investment.asset_object?.site_name) {
      siteName = this.investment.asset_object.site_name;
    } else if (this.investment.site_name) {
      siteName = this.investment.site_name;
    } else {
      const site = this.investment.perimeters.find(p => p.building_id === null);
      siteName = site?.name || 'Unknown Site';
    }

    if (perimeter.parentPerimeter) {
      return `${siteName} > ${perimeter.parentPerimeter.name} > ${perimeter.name}`;
    }

    if (perimeter.level_parent) {
      const parentPerimeter = this.findParentPerimeterDirect(perimeter.level_parent);
      if (parentPerimeter) {
        return `${siteName} > ${parentPerimeter.name} > ${perimeter.name}`;
      }
      return `${siteName} > ${perimeter.name}`;
    }

    return `${siteName} > ${perimeter.name}`;
  }

  private findParentPerimeterDirect(parentId: number): KPI.Perimeter | null {
    const assetPerimeters = this.investment.asset_object?.perimeters || [];
    for (const perimeter of assetPerimeters) {
      if (perimeter && perimeter.id === parentId) {
        return perimeter;
      }
    }

    for (const perimeter of this.investment.perimeters) {
      if (perimeter && perimeter.id === parentId) {
        return perimeter;
      }
    }

    return null;
  }

  public getFormattedInvestmentNotation(): string {
    if (!this.investment) {
      return "";
    }
    if (this.investment.investment_type_object?.replacement === true) {
      return this.translate.instant('New');
    }
    if (this.investment.has_no_impact) {
      return this.translate.instant('No impact');
    }
    let notation = this.investment.technical_state_notation;
    if (notation === null || notation === undefined) {
      const investmentNotations = (this.investment as any).investment_notations;
      if (investmentNotations && Array.isArray(investmentNotations) && investmentNotations.length > 0) {
        const validNotations = investmentNotations.filter((n: any) => this.technicalStateQuestionItemIds.has(n.question_item));
        const lastRecord = validNotations[validNotations.length - 1];
        notation = lastRecord?.note;
      }
    }
    if (notation === null || notation === undefined) {
      return this.translate.instant('No impact');
    }
    const percentage = this.stateLevelsService.getTechnicalStateNotationFromLevelOrNote(notation);
    return this.stateLevelsService.getTechnicalStateLabelFromNote(percentage);
  }

  public shouldHideGtpCode(): boolean {
    const statusObject = this.investment?.status_object;
    return statusObject && (
      statusObject.hypothesis === true ||
      statusObject.is_request === true ||
      statusObject.is_proposed === true
    );
  }

  public selectBudgetScope() {
    // The budget_scopes field is an array. But now that we want a single choice list,
    // we have to convert the choice into an array.
    this.form.patchValue({
      budget_scopes: [this.selectedBudgetScopeId],
    });
    this.form.controls['budget_scopes'].markAsDirty();
  }

  getInvestmentTaxonomy() {
    return (this.investment.taxonomy_objective || this.investment.taxonomy_category) ? this.translate.instant('Yes') : this.translate.instant('No');
  }

  getStatusDisplay(): string {
    if (!this.form?.get('status')?.value) return '-';
    const statusId = this.form.get('status').value;
    return this.statusesMap.get(statusId) || '-';
  }

  getPriorityDisplay(): string {
    if (!this.form?.get('priority')?.value) return '-';
    const priorityId = this.form.get('priority').value;
    return this.prioritiesMap.get(priorityId) || '-';
  }

  getImpactDisplay(): string {
    if (!this.form?.get('impact_roadmap_indicators')?.value) return '-';
    const impactIds = this.form.get('impact_roadmap_indicators').value;
    if (!Array.isArray(impactIds) || impactIds.length === 0) return '-';
    const impactNames = impactIds
      .map((id: number) => this.impactsMap.get(id))
      .filter((name: string | undefined) => name);
    return impactNames.length > 0 ? impactNames.join(', ') : '-';
  }

  getReasonsDisplay(): string {
    const reasonId = this.form?.get('reasons')?.value;
    if (reasonId == null) return '-';
    return this.reasonsMap.get(reasonId) || '-';
  }

  getFurtherInformationDisplay(): string {
    if (!this.form?.get('further_information')?.value) return '-';
    const furtherInfoId = this.form.get('further_information').value;
    return this.furtherInfosMap.get(furtherInfoId) || '-';
  }

  getInvestmentCategoryDisplay(): string {
    if (!this.form?.get('investment_category')?.value) return '-';
    const categoryId = this.form.get('investment_category').value;
    const category = this.ivCategories()?.find(cat => cat.id === categoryId);
    return category?.name || '-';
  }

  getCommentsDisplay(): string {
    return this.form?.get('comments')?.value || '-';
  }

  private statusReadOnly(status: KPI.InvestmentStatus): boolean {
    return this.readOnly || status?.is_closed || status?.is_validated || status?.is_locked;
  }

  public openDialogModify(type: 'BUDGET_ALLOCATION' | 'GAIN_ENERGY_CARBON' | 'MONO_PERIMETER_SELECTION') {
    if (type === 'GAIN_ENERGY_CARBON' && this.isLocked) {
      return;
    }
    const scope = this.investment.budget_scopes && this.investment.budget_scopes.length > 0
      ? this.budgetScopes.find(scope => scope.id === this.investment.budget_scopes[0])
      : null;

    this.ivWizardService.setState('INVESTMENT_TYPE_SELECTION', this.investment.investment_type_object);
    this.ivWizardService.setState('SITE_SELECTION', this.investment.perimeters?.find(p => !p.is_monosite));
    const budgetAllocationState = {
      price: <number>this.investment.slices[0]?.price,
      final_schedule_to: this.investment.final_schedule_to,
      status: this.investment.status_object,
      budget_origin: this.investment.budget_origin,
      priority: this.investment.priority,
      budget_scopes: scope,
      is_capex: this.investment.is_capex,
      aligned_taxonomy: false,
      price_sheet_id: this.investment.price_sheet,
    };
    this.ivWizardService.setState('BUDGET_ALLOCATION', budgetAllocationState);
    this.ivWizardService.setState('ASSET_SELECTION', this.investment.asset_object);
    const initiative = this.investment.initiative,
      iniType = this.#iniTypes().find(type => type.id === initiative?.initiative_type);
    let iniOb$;
    if (initiative?.id) {
      iniOb$ = this.#iniService.getInitiative(initiative.id).pipe(tap(initiative => {
        const mappedGains = (initiative?.gains || []).map((gain: any) => ({
          id: gain.id,
          initiative_id: gain.initiative_id,
          energy_type_id: gain.energy_type_id || null,
          refrigerant_type_id: gain.refrigerant_type_id || null,
          gain_origin_id: gain.gain_origin?.id || null,
          gain_precision_id: gain.gain_precision?.id || null,
          theoretical_savings_full_year: gain.theoretical_savings_full_year,
          theoretical_savings_first_year: gain.theoretical_savings_first_year,
          theoretical_greenhouse_gas_savings_full_year: gain.theoretical_greenhouse_gas_savings_full_year,
          theoretical_greenhouse_gas_savings_first_year: gain.theoretical_greenhouse_gas_savings_first_year,
          real_savings: gain.real_savings,
          real_greenhouse_gas_savings: gain.real_greenhouse_gas_savings,
          full_stop: gain.full_stop,
        }));

        this.ivWizardService.setState('GAIN_ENERGY_CARBON', {
          enabled: !!initiative,
          responsible: initiative.responsible,
          initially_proposed_by: initiative?.initially_proposed_by || null,
          description: initiative?.description || null,
          initiative_type: iniType,
          gains: mappedGains,
        });
      }));
    } else {
      iniOb$ = of(null);
    }

    if (type === 'MONO_PERIMETER_SELECTION') {
      const site = this.investment.multi_perimeter ||
        this.investment.perimeters.find(p => p.building_id === null);
      this.ivWizardService.setState('SITE_SELECTION', site);
      this.ivWizardService.setState('MONO_PERIMETER_SELECTION', this.selectedPerimeter);
      this.ivWizardService.next('MONO_PERIMETER_SELECTION');
      iniOb$ = of(null);
    }

    iniOb$.subscribe(() => {
      const readOnly = this.statusReadOnly(this.investment.status_object);
      const ref = this.sideFormLevelService.open(
        InvestmentFormWrapperComponent, {
          closeOnBackdropClick: false,
          context: {
            type,
            investment: this.investment,
            readOnly,
            forceReadOnly: this.readOnly,
            allowedStatuses: this.allowedStatuses,
          },
        });
      ref.onClose.subscribe((updatedObject) => {
        if (updatedObject) {
          this.ivWizardService.onDestroy();
          if (updatedObject.deleted) {
            if (type === 'BUDGET_ALLOCATION') {
              this.form.patchValue({ price: null });
              this.form.markAsDirty();
            } else if (type === 'GAIN_ENERGY_CARBON') {
              if (this.investment.initiative) {
                this.investment.initiative = null;
              }
            }
            this.close();
            return;
          }

          if (type === 'BUDGET_ALLOCATION') {
            this.investmentsService.getInvestment(this.investment.id).subscribe((freshInvestment) => {
              if (freshInvestment) {
                this.investment = freshInvestment as any;
                this.initialValues.budget_origin = this.investment.budget_origin?.id;
                this.initialValues.year = this.investment.slices[0]?.year;
                this.initialValues.price = this.investment.slices[0]?.price;
                this.initialValues.budget_scopes = this.investment.budget_scopes;
                this.initialValues.price_sheet = this.investment.price_sheet;
                this.initialValues.status = this.investment.status;
                this.form.patchValue({
                  year: this.investment.slices[0]?.year,
                  price: this.investment.slices[0]?.price,
                  budget_scopes: this.investment.budget_scopes,
                  budget_origin: this.investment.budget_origin?.id,
                  price_sheet: this.investment.price_sheet,
                  status: this.investment.status,
                }, { emitEvent: false });
                this.setUpAvailableValues();
                this.refreshFormDisabledState();
                this.cdr.detectChanges();
              }
            });
          } else if (type === 'GAIN_ENERGY_CARBON') {
            if (updatedObject.initiative) this.investment.initiative = updatedObject.initiative;
            this._gainUpdated.set(this._gainUpdated() + 1);
          }
          this.form.markAsPristine();
          this.chartsService.capexHasChanged();
        } else if (type === 'MONO_PERIMETER_SELECTION') {
          const selected = this.ivWizardService.getState('MONO_PERIMETER_SELECTION');
          if (selected && selected.id !== this.selectedPerimeter?.id) {
            this.selectedPerimeter = selected;
            this.form.get('perimeter').setValue(selected.id);
            this.autoSave('perimeter');
            this.cdr.detectChanges();
          }
        }
        this.ivWizardService.onDestroy();
      });
    });
  }

  private showDeleteInvestmentDialog() {
    const content = this.translate.instant('By deleting the price, you will delete all the investment\'s data');
    const dialogRef = this.dialogService.open(
      BasicDialogComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Delete investment'),
          content: content,
          canCancel: false
        },
      },
    );
    this.subscriptions.push(
      dialogRef.onClose.pipe(
        filter(confirm => !!confirm),
        switchMap(() => {
          return this.investmentsService.deleteInvestment(this.investment.id);
        }),
      ).subscribe({
        next: () => {
          this.remainingFlashBudget$ = of(null);
          this.chartsService.capexHasChanged();
          this.nbDialogRef.close({ deleted: true });
        },
        error: (error) => {
          console.error('[InvestmentForm] Error deleting investment:', error);
          this.saveToast.showError('Failed to delete investment');
        },
      }),
    );
  }

  public get canDeleteInvestment(): boolean {
    const status = this.investment.status_object;
    const enabled = !!(status?.is_cancelled || status?.hypothesis || status?.is_request);
    return enabled && !status?.is_locked && !this.readOnly && !!this.customerConfig?.is_show_edit;
  }

  public onDeleteMenuItemClick(popover?: NbPopoverDirective): void {
    if (!this.canDeleteInvestment) {
      return;
    }
    popover?.hide();
    this.confirmDeleteInvestment();
  }

  private confirmDeleteInvestment(): void {
    if (!this.canDeleteInvestment) {
      return;
    }
    const content = this.translate.instant('Do you confirm that {elementName} will be deleted ?', {
      elementName: this.investment.label,
    });
    const initiativeId = this.investment.initiative?.id;
    const option = initiativeId ? this.translate.instant('Also delete the linked actions') : '';

    const dialogRef = this.dialogService.open(
      DialogWithCheckboxOptionComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('Delete'),
          content,
          option,
        },
      },
    );
    this.subscriptions.push(
      dialogRef.onClose.pipe(
        filter((dialogData) => !!dialogData?.confirmation),
        switchMap((dialogData) =>
          this.investmentsService.deleteInvestment(this.investment.id).pipe(
            switchMap(() => (dialogData.checked && initiativeId) ? this.#iniService.deleteInitiative(initiativeId) : of(null)),
          ),
        ),
      ).subscribe({
        next: () => {
          this.chartsService.capexHasChanged();
          this.nbDialogRef.close({ deleted: true });
        },
        error: (error) => {
          console.error('[InvestmentForm] Error deleting investment:', error);
          this.saveToast.showError('Failed to delete investment');
        },
      }),
    );
  }

  public openPerimeterSelection() {
    if (this.readOnly || this.investment.asset) return;
    this.openDialogModify('MONO_PERIMETER_SELECTION' as any);
  }

  public onRefacturationChange(value: boolean | null) {
    (this.investment as any).has_invoicing = value ?? null;
    this.autoSave('has_invoicing');
  }

  public openTaxonomyDialog() {
    if (this.readOnly || this.isLocked) {
      return;
    }

    const dialogRef = this.sideFormLevelService.open(
      InvestmentFormWrapperComponent,
      {
        closeOnBackdropClick: false,
        context: {
          type: 'TAXONOMY_SELECTION' as any,
          investment: this.investment,
          readOnly: this.readOnly || this.isLocked,
        },
      }
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        // Update investment properties that exist on the model
        if (result.taxonomy_objective !== undefined) {
          this.investment.taxonomy_objective = result.taxonomy_objective;
        }
        if (result.taxonomy_category !== undefined) {
          this.investment.taxonomy_category = result.taxonomy_category;
        }

        // Update the form control and initial values to reflect the change
        const newTaxonomyValue = this.getInvestmentTaxonomy();
        this.initialValues.aligned_taxonomy = newTaxonomyValue;
        this.form.patchValue({
          aligned_taxonomy: newTaxonomyValue
        });

        // Save all changes including comment
        const changes: { property: string; value: any }[] = [];
        if (result.taxonomy_objective !== undefined) {
          changes.push({
            property: 'taxonomy_objective',
            value: result.taxonomy_objective?.id || null,
          });
        }
        if (result.taxonomy_category !== undefined) {
          changes.push({
            property: 'taxonomy_category',
            value: result.taxonomy_category?.id || null,
          });
        }
        if (result.taxonomy_comment !== undefined) {
          changes.push({
            property: 'taxonomy_comment',
            value: result.taxonomy_comment || '',
          });
        }

        if (changes.length > 0 && this.investment.id) {
          this.saveToast.showSaving();
          this.investmentsChanges.addChanges(this.investment.id, changes);
          this.investmentsChanges.pendingChangesNumber$.pipe(
            filter((nb) => !nb),
            first(),
          ).subscribe(() => this.saveToast.showSaved());
        }

        this.cdr.detectChanges();
      }
    });
  }

  public openCategorySelector() {
    if (this.readOnly || this.isLocked || this.investment.asset) {
      if (this.investment.asset) {
        // Show a message that category cannot be changed when linked to equipment
        return;
      }
      return;
    }

    const dialogRef = this.sideFormLevelService.open(
      InvestmentFormWrapperComponent,
      {
        closeOnBackdropClick: false,
        context: {
          type: 'CATEGORY_SELECTION' as any,
          investment: this.investment,
          readOnly: this.readOnly || this.isLocked || !!this.investment.asset,
        },
      }
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        // Update investment properties
        if (result.super_category !== undefined) {
          this.investment.super_category = result.super_category?.id || null;
          this.investment.super_category_object = result.super_category;
        }
        if (result.category !== undefined) {
          this.investment.category = result.category?.id || null;
          this.investment.category_object = result.category;
        }
        if (result.sub_category !== undefined) {
          this.investment.sub_category = result.sub_category?.id || null;
          this.investment.sub_category_object = result.sub_category;
        }

        // Update the form control and initial values
        const newCategoryValue = this.getTechnicalCategoryDisplay();
        this.initialValues.technical_category = newCategoryValue;
        this.form.patchValue({
          technical_category: newCategoryValue
        });

        // Save the changes
        const changes: { property: string; value: any }[] = [];
        if (result.super_category !== undefined) {
          changes.push({
            property: 'super_category',
            value: result.super_category?.id || null,
          });
        }
        if (result.category !== undefined) {
          changes.push({
            property: 'category',
            value: result.category?.id || null,
          });
        }
        if (result.sub_category !== undefined) {
          changes.push({
            property: 'sub_category',
            value: result.sub_category?.id || null,
          });
        }

        if (changes.length > 0 && this.investment.id) {
          this.investmentsChanges.addChanges(this.investment.id, changes);
        }

        this.cdr.detectChanges();
      }
    });
  }

  public autoSave(field?: string) {
    if (this.inWizard || this.initiative || !this.investment?.id || this.readOnly || this.hasPendingChanges) {
      return;
    }
    if (field && !this.fieldValueChanged(field)) {
      return;
    }
    if (this.isFormValid() && this.loading === false) {
      this.validateForm(false);
    }
  }

  private setSaving(): void {
    this.saveToast.showSaving();
  }

  private setSaved(): void {
    this.saveToast.showSaved();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
  }


  public openLinkedEquipmentDialog(): void {
    const investmentBeforeChanges = this.investment;
    const site = this.investment.multi_perimeter ? this.investment.multi_perimeter
     : this.investment.perimeters.find(
      perimeter => !perimeter.is_monosite
    );
    this.sideFormLevelService.open(LinkedEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'document-sheet-backdrop',
      dialogClass: 'doc-linked-equipment-overlay',
      context: {
        linkedEquipmentIds: this.investment.asset ? [this.investment.asset] : [],
        asset: null,
        reportId: null,
        siteId: site?.id,
        title: this.translate.instant('Equipment related to the investment'),
        investmentId: this.investment.id,
        multipleChoice: false,
        readOnly: this.readOnly,
        investmentType: this.investment.investment_type_object
      } as any,
    }).onClose.pipe(first()).subscribe((linkedAssetIds: number[]) => {
      // The investment will be reloaded after attaching/detaching the equipment, but we need
      // to check if a new equipment has been attached so we can open the investment notation
      if (linkedAssetIds?.length > 0 && linkedAssetIds[0] !== investmentBeforeChanges.asset) {
        this.openInvestmentNotation();
      }
    });
  }

  public openInvestmentNotation() {
    if (!this.investment.asset) {
      return;
    }
    const scope = this.investment.budget_scopes && this.investment.budget_scopes.length > 0
      ? this.budgetScopes.find(scope => scope.id === this.investment.budget_scopes[0])
      : null;

    this.ivWizardService.setState('INVESTMENT_TYPE_SELECTION', this.investment.investment_type_object);
    const budgetAllocationState = {
      price: <number>this.investment.slices[0]?.price,
      final_schedule_to: this.investment.final_schedule_to,
      status: this.investment.status_object,
      budget_origin: this.investment.budget_origin,
      priority: this.investment.priority,
      budget_scopes: scope,
      is_capex: this.investment.is_capex,
      aligned_taxonomy: false,
      price_sheet_id: this.investment.price_sheet,
    };
    this.ivWizardService.setState('BUDGET_ALLOCATION', budgetAllocationState);
    let loadedAsset: KPI.MappedAsset = null;
    let asset$: Observable<KPI.MappedAsset> = of(null);
    let newNote: number = null;
    let hasImpact: boolean = false;
    if (this.investment.asset_object) {
      asset$ = of(this.investment.asset_object);
    } else {
      asset$ = this.assetsService.getAsset(this.investment.asset);
    }

    this.subscriptions.push(
      asset$.pipe(
        switchMap(asset => {
          loadedAsset = asset;
          this.ivWizardService.setState('ASSET_SELECTION', loadedAsset);
          this.ivWizardService.investmentId = this.investment.id;

          let existingLevel = this.investment.technical_state_notation;
          if (existingLevel == null) {
            const notations = (this.investment as any).investment_notations;
            if (Array.isArray(notations) && notations.length > 0) {
              const validNotations = notations.filter((n: any) => this.technicalStateQuestionItemIds.has(n.question_item));
              const lastRecord = validNotations[validNotations.length - 1];
              existingLevel = lastRecord?.note ?? null;
            }
          }
          let notationStepPreFill: any = null;
          const enabled = !this.investment.has_no_impact;
          if (existingLevel != null) {
            const percentage = this.stateLevelsService.getTechnicalStateNotationFromLevelOrNote(existingLevel);
            const nearestKey = this.stateLevelsService.getTechnicalStateOptionNoteFromNotation(percentage);
            notationStepPreFill = { enabled, note: nearestKey };
          } else if (!enabled) {
            notationStepPreFill = { enabled: false, note: null };
          }

          const state: any = {
            'INVESTMENT_TYPE_SELECTION': this.investment.investment_type_object,
            'ASSET_SELECTION': asset,
            'SITE_SELECTION': this.investment.perimeters?.find(p => !p.is_monosite),
            'BUDGET_ALLOCATION': budgetAllocationState,
            ...(notationStepPreFill ? { 'INVESTMENT_NOTATION_STEP': notationStepPreFill } : {}),
          };
          this.ivWizardService.prefill(state, 'INVESTMENT_NOTATION_STEP');

          this.documentsService.childDialogOpen = true;
          return this.sideFormLevelService.open(InvestmentWizardRootComponent, {
            autoFocus: false,
            closeOnBackdropClick: true,
            hasScroll: false,
            hasBackdrop: true,
            backdropClass: 'document-sheet-backdrop',
            dialogClass: 'doc-linked-equipment-overlay',
          }).onClose.pipe(
            finalize(() => { this.documentsService.childDialogOpen = false; })
          )
        }),
        filter((investmentNotation: InvestmentNotationStepState) => !!investmentNotation),
        switchMap((investmentNotation: InvestmentNotationStepState) => {
          newNote = investmentNotation.note;
          hasImpact = investmentNotation.enabled;
          let saveNoImpactField$ = of(0);
          const hasNoImpact = !investmentNotation.enabled;
          if (hasNoImpact !== this.investment.has_no_impact) {
            // It's important to save the "has_no_impact" field before the notation, or else
            // the backend deletes the investment notation right after creating it.
            this.investment.has_no_impact = hasNoImpact;
            this.investmentsChanges.addChanges(this.investment.id, [{
              property: 'has_no_impact',
              value: hasNoImpact,
            }]);
            saveNoImpactField$ = this.investmentsChanges.pendingChangesNumber$.pipe(
              filter(pendingChangesNumber => pendingChangesNumber === 0),
              first(),
            );
          }
          this.ivWizardService.setState('INVESTMENT_TYPE_SELECTION', this.investment.investment_type_object);
          this.ivWizardService.setState('ASSET_SELECTION', loadedAsset);
          this.ivWizardService.setState('INVESTMENT_NOTATION_STEP', investmentNotation);
          return saveNoImpactField$.pipe(
            switchMap(() => this.ivWizardService.createInvestmentNotationAndDurationDeviation(this.investment, true))
          )
        })
      ).subscribe(() => {
        const impactLabel = hasImpact && newNote !== null
          ? this.stateLevelsService.getTechnicalStateLabelFromNote(newNote)
          : this.translate.instant('No impact');
        this.form?.get('investment_notation')?.setValue(impactLabel);
      })
    )
  }

  public showLinkedEquipement() {
    return !!this.investment.id;
  }

  public spendingCategoryClicked() {
    if (this.form.get('investment_category')?.disabled) {
    } else {
      this.openSpendingCategories();
    }
  }

  private openSpendingCategories() {
    this.ivWizardService.investmentId = this.investment.id;
    this.ivWizardService.existingInvestment = this.investment;

    const categoryId = this.form.get('investment_category').value;
    if (categoryId) {
      const category = this.ivCategories()?.find(cat => cat.id === categoryId);
      this.ivWizardService.setState('INVESTMENT_CATEGORY_SELECTION', category)
    }
    this.sideFormLevelService.open(
      InvestmentWizardQuickEditComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          stepKey: "INVESTMENT_CATEGORY_SELECTION",
        },
      },
    ).onClose.subscribe(() => {
      const newCategory = this.ivWizardService.getState("INVESTMENT_CATEGORY_SELECTION");
      if (newCategory && newCategory.id !== categoryId) {
        this.form.patchValue({
          investment_category: newCategory.id
        })
      }
      this.ivWizardService.removeStates(["INVESTMENT_CATEGORY_SELECTION"]);
      this.ivWizardService.investmentId = null;
      this.ivWizardService.existingInvestment = null;
    })
  }
}
